import { action, flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { ItemApi } from '@/apis';
import { toastify } from '@/utils/toastify';
import { t } from 'i18next';
import { PagingRequest, ResponseData } from '@/types/http';
import { BodyEditRegisteredItem, Item, ItemColected, ItemOnMap, OwnerItem, ParamsGetItemNear, ParamsRequestListDropItem, ParamsRequestListRegistered } from '@/types/item';
import FileApi from '@/apis/FileApi';
import { getStorage, saveLocalStorage } from '@/utils/browsers';

export default class ItemsStore extends BaseStore {
    api: ItemApi;
    fileApi: FileApi;
    listItemsColected: OwnerItem[] = [];
    listOwnerItems: OwnerItem[] = [];
    listItems: OwnerItem[] = [];
    listItemRegisted: OwnerItem[] = [];
    listNearBy: ItemOnMap[] = [];
    itemToSetLocation: OwnerItem | null = null;
    listItemToSetLocation: OwnerItem[] | null = null;
    detailItem?: OwnerItem;
    detailItemRegistered?: OwnerItem;
    paramsNearBy: ParamsGetItemNear = getStorage('paramsNearBy', true);
    total: number = 0;
    idDetail: any = null;
    itemLocations: any[] | null = null;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            listItemsColected: observable,
            listOwnerItems: observable,
            listItems: observable,
            listItemRegisted: observable,
            listNearBy: observable,
            detailItem: observable,
            idDetail: observable,
            itemLocations: observable,
            detailItemRegistered: observable,
            itemToSetLocation: observable,
            listItemToSetLocation: observable,
            getListColected: flow.bound,
            getListAllItem: flow.bound,
            getListItems: flow.bound,
            putListItemsAssagined: flow.bound,
            getDetailItemCollected: flow.bound,
            putUseItem: flow.bound,
            getListItemRegistered: flow.bound,
            deleteRegisteredItem: flow.bound,
            getDetailItemRegistered: flow.bound,
            uploadFile: flow.bound,
            registerNewItem: flow.bound,
            updateRegisteredItemById: flow.bound,
            getListNearBy: flow.bound,
            collectItem: flow.bound,
            reportItem: flow.bound,
            total: observable,
            setIdDetail: action.bound,
            setItemLocations: action.bound,
            setItemToSetLocation: action.bound
        });
        this.api = new ItemApi();
        this.fileApi = new FileApi();
    }

    setIdDetail(id: any) {
        this.idDetail = id;
    }

    setItemLocations(listtItem: any[] | null) {
        this.itemLocations = listtItem;
    }

    setItemToSetLocation(item: OwnerItem | null, listItem: OwnerItem[] | null = []) {
        this.itemToSetLocation = item;
        this.listItemToSetLocation = listItem;
    }

    * getListColected(params: ParamsRequestListDropItem) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getListColected, params);
            if (res && res.ok) {
                const { data } = res.data;
                this.listItemsColected = data || [];
                this.total = res.data.paginate.totalRecord;
                return data || [];
            }
            return [];
        } catch (error) {
            return [];
        }
    }

    * getListItems(params: PagingRequest = { page: 1, size: 10 }) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getListItem, params);
            if (res?.ok) {
                this.listItems = res.data.elements || [];
                this.total = res.data.paginate.totalRecord;
            }
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }

    * getListAllItem() {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getListAllItem);
            if (res?.ok) {
                this.listOwnerItems = res.data.elements || [];
            }
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }

    * putListItemsAssagined(payload) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.putListItemAssigned, payload);
            if (res.ok) {
                this.getListItems();
                return res.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    * getDetailItemCollected(payload) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.detailItemCollected, payload);
            if (res.ok && res.data) {
                this.detailItem = res.data;
                return res.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    * putUseItem(payload) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.useItem, payload);
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }

    * getListItemRegistered(payload: ParamsRequestListRegistered) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getListItemRegistered, payload);
            if (res.ok && res.data) {
                const { elements } = res.data;
                this.listItemRegisted = elements || [];
                this.total = res.data.paginate.totalRecord;
                return elements;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    * deleteRegisteredItem(id) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.deleteItemRegistered, id);
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }

    * getDetailItemRegistered(id) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getDetailItemRegistered, id);
            if (res.ok) {
                const { data } = res;
                this.detailItemRegistered = data || {};
            }
        } catch (error) {
            return false;
        }
    }

    * uploadFile(file: File) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.fileApi.uploadImage,
                formData
            );
            if (res.ok) {
                const { id } = res.data;
                return id || false;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    * registerNewItem(payload) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.registerNewItem, payload);
            if (res.ok) {
                toastify('success', t('messages.addSuccess'));
            } else {
                toastify('error', t('messages.errorTryAgain'));
            }
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }

    * updateRegisteredItemById(body) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.editRegisteredItem, body);
            if (res.ok) {
                toastify('success', t('messages.updateSuccess'));
            } else {
                toastify('error', t('messages.errorTryAgain'));
            }
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }

    * getListNearBy(params: ParamsGetItemNear | undefined): ItemOnMap[] | any {
        try {
            const res: ResponseData<ItemOnMap[]> = yield this.rootStore.apiStore.call(this.api, this.api.getItemNearUser, params ? params : this.paramsNearBy, { hideLoading: true });
            if (res.ok) {
                if (params) {
                    this.paramsNearBy = params;
                    saveLocalStorage('paramsNearBy', JSON.stringify(params));
                }
                this.listNearBy = res.data || [];
                return res.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    * collectItem(id) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.colectItemSuccess, id);
            if (!res.ok) {
                toastify('error', res.message || t('messages.errorTryAgain'));
            }
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }

    * reportItem(id) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.reportItem, id);
            if (!res.ok) {
                toastify('error', res.message || t('messages.errorTryAgain'));
            }
            return res.ok || false;
        } catch (error) {
            return false;
        }
    }
}
